import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";
import { RefreshCw, Clock, Menu, ShieldCheck, Eye } from "lucide-react";
import api from "@/lib/api";
import { SEGMENTS } from "@/lib/segments";
import { useAuth } from "@/context/AuthContext";
import Sidebar from "@/components/dashboard/Sidebar";
import KpiCards from "@/components/dashboard/KpiCards";
import { TrendChart, SourceChart, CityChart } from "@/components/dashboard/Charts";
import ProfilesTable from "@/components/dashboard/ProfilesTable";
import PhoneGroups from "@/components/dashboard/PhoneGroups";
import SourcesPanel from "@/components/dashboard/SourcesPanel";
import DailyCollection from "@/components/dashboard/DailyCollection";
import HighlightsStrip from "@/components/dashboard/HighlightsStrip";
import DestaquesCarousel from "@/components/dashboard/DestaquesCarousel";
import WelcomeBanner from "@/components/dashboard/WelcomeBanner";
import AISearch from "@/components/dashboard/AISearch";
import AlertsFeed from "@/components/dashboard/AlertsFeed";
import FavoritesView from "@/components/dashboard/FavoritesView";
import CommentsView from "@/components/dashboard/CommentsView";
import { WhatsAppProvider } from "@/context/WhatsAppContext";
import WhatsAppSettingsDialog from "@/components/dashboard/WhatsAppSettingsDialog";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";

function timeAgo(iso) {
  if (!iso) return "nunca";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "agora mesmo";
  if (diff < 3600) return `há ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `há ${Math.floor(diff / 3600)} h`;
  return new Date(iso).toLocaleString("pt-PT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

const TITLES = {
  overview: "Visão Geral",
  alerts: "Alertas e Atividade",
  ai: "Pesquisa IA",
  profiles: "Todos os Perfis",
  favorites: "Os Meus Favoritos",
  duplicates: "Números Duplicados",
  comments: "Comentários dos Sites",
  sources: "Origens",
};

export default function DashboardPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [view, setView] = useState("overview");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [category, setCategory] = useState(""); // "" | mulheres | trans | homens | massagens
  const [profileSource, setProfileSource] = useState(""); // source filter from clicks
  const [profileOnlyNew, setProfileOnlyNew] = useState(false);
  const [stats, setStats] = useState(null);
  const [sources, setSources] = useState([]);
  const [lastRun, setLastRun] = useState(null);
  const [running, setRunning] = useState(false);
  const [alertCount, setAlertCount] = useState(0);
  const [waSettingsOpen, setWaSettingsOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [defaultCriteria, setDefaultCriteria] = useState(null);
  const appliedDefault = useRef(false);
  const poll = useRef(null);

  const loadAll = useCallback(async () => {
    const [s, src, st] = await Promise.all([
      api.get("/stats", { params: category ? { category } : {} }),
      api.get("/sources"),
      api.get("/scrape/status"),
    ]);
    setStats(s.data);
    setSources(src.data);
    setLastRun(st.data.last_run);
    setRunning(st.data.running);
    try {
      const { data } = await api.get("/alerts/summary", { params: { days: 7 } });
      setAlertCount(data?.total_24h || 0);
    } catch (e) { console.debug("alerts summary unavailable:", e); }
    return st.data;
  }, [category]);

  useEffect(() => {
    // Deferred to a microtask so the effect body doesn't synchronously invoke a
    // state setter (keeps behaviour identical; avoids set-state-in-effect lint).
    Promise.resolve().then(loadAll);
  }, [loadAll]);

  // Polling re-uses loadAll (the only place that calls setState) so neither the
  // interval nor any effect references setState directly.
  const startPolling = useCallback(() => {
    clearInterval(poll.current);
    poll.current = setInterval(async () => {
      const status = await loadAll();
      if (!status?.running) {
        clearInterval(poll.current);
        toast.success("Recolha concluída", {
          description: `${status?.last_run?.new_total || 0} novos perfis encontrados`,
        });
      }
    }, 3000);
  }, [loadAll]);

  useEffect(() => () => clearInterval(poll.current), []);

  // On first entry, if the user has a default saved filter, open the Profiles
  // view already filtered by it (their "home" view).
  useEffect(() => {
    if (appliedDefault.current) return;
    appliedDefault.current = true;
    (async () => {
      try {
        const { data } = await api.get("/me/filters");
        const def = (data.items || []).find((f) => f.id === data.default_id);
        if (def) {
          setDefaultCriteria(def.criteria || {});
          setView("profiles");
        }
      } catch (e) { console.debug("no saved default filter:", e); }
    })();
  }, []);

  const refresh = async () => {
    try {
      const { data } = await api.post("/scrape");
      if (data.status === "already_running") {
        toast.info("Já existe uma recolha em curso");
      } else {
        toast.info("Recolha iniciada — a visitar os sites…");
      }
      setRunning(true);
      startPolling();
    } catch (e) {
      toast.error("Não foi possível iniciar a recolha");
    }
  };

  // Clickable overview → deep-link into the right view with the right filter.
  const openView = ({ view: v = "profiles", source = "", onlyNew = false }) => {
    setDefaultCriteria(null);
    setProfileSource(source);
    setProfileOnlyNew(onlyNew);
    if (v !== "duplicates") setCategory("");
    setView(v);
  };

  // Sidebar navigation resets any click-through filters so each section opens clean.
  const navSetView = (v) => {
    setDefaultCriteria(null);
    setProfileSource("");
    setProfileOnlyNew(false);
    setView(v);
  };

  return (
    <WhatsAppProvider>
    <div className="flex bg-[#09090b] min-h-screen">
      <Sidebar view={view} setView={navSetView} alertCount={alertCount}
        mobileOpen={sidebarOpen} onClose={() => setSidebarOpen(false)}
        onOpenSettings={() => setWaSettingsOpen(true)}
        onOpenPassword={() => setPwOpen(true)} />

      <main className="flex-1 min-w-0">
        {/* Header */}
        <header className="sticky top-0 z-10 bg-[#09090b]/90 backdrop-blur border-b border-[#27272a] px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <button
              type="button"
              onClick={() => setSidebarOpen(true)}
              data-testid="mobile-menu-btn"
              className="lg:hidden text-[#a1a1aa] hover:text-[#fafafa] -ml-1 p-1"
              aria-label="Abrir menu"
            >
              <Menu className="w-6 h-6" />
            </button>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="font-head text-lg sm:text-xl font-normal text-[#fafafa] truncate">{TITLES[view]}</h1>
                <span
                  data-testid="view-role-badge"
                  className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 border ${
                    isAdmin
                      ? "border-[#c8aa6e]/40 text-[#c8aa6e] bg-[#c8aa6e]/10"
                      : "border-[#27272a] text-[#a1a1aa] bg-[#18181b]"
                  }`}
                >
                  {isAdmin ? <ShieldCheck className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                  {isAdmin ? "Vista de administrador" : "Vista de cliente · apenas leitura"}
                </span>
              </div>
              <div className="flex items-center gap-2 text-xs text-[#71717a] mt-1">
                <Clock className="w-3 h-3 shrink-0" />
                <span className="truncate">Última sincronização: {timeAgo(lastRun?.finished_at || lastRun?.started_at)}</span>
                {running && <span className="text-[#c8aa6e]">· em curso…</span>}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-4 ml-auto">
            {(view === "overview" || view === "profiles") && (
              <div className="flex border border-[#27272a] overflow-x-auto" data-testid="category-filter">
                {[{ k: "", label: "Todos" }, ...SEGMENTS].map((c) => (
                  <button
                    key={c.k}
                    data-testid={`cat-${c.k || "all"}`}
                    onClick={() => setCategory(c.k)}
                    className={`px-2.5 sm:px-3 py-2 text-xs whitespace-nowrap transition-colors ${
                      category === c.k
                        ? "bg-[#c8aa6e] text-black"
                        : "text-[#a1a1aa] hover:text-[#fafafa]"
                    }`}
                  >
                    {c.label}
                    {stats?.per_category && c.k && (
                      <span className="ml-1.5 opacity-70">{stats.per_category[c.k] || 0}</span>
                    )}
                  </button>
                ))}
              </div>
            )}
            {isAdmin ? (
              <button
                data-testid="btn-refresh-sync"
                onClick={refresh}
                disabled={running}
                className="bg-[#c8aa6e] text-black hover:bg-[#d4b982] active:scale-[0.98] px-3 sm:px-4 py-2.5 font-medium text-sm transition-all flex items-center gap-2 disabled:opacity-60 shrink-0"
              >
                <RefreshCw className={`w-4 h-4 ${running ? "spin" : ""}`} />
                <span className="hidden sm:inline">{running ? "A recolher…" : "Atualizar Agora"}</span>
              </button>
            ) : (
              <div
                data-testid="readonly-badge"
                title="Os dados são recolhidos automaticamente todos os dias. Apenas leitura."
                className="flex items-center gap-2 border border-[#27272a] bg-[#18181b] text-[#a1a1aa] px-3 sm:px-4 py-2.5 text-sm shrink-0"
              >
                <RefreshCw className={`w-4 h-4 text-[#10b981] ${running ? "spin" : ""}`} />
                <span className="hidden sm:inline">{running ? "A atualizar dados…" : "Atualização automática"}</span>
              </div>
            )}
          </div>
        </header>

        <div className="p-4 sm:p-6 lg:p-8 space-y-6 fade-up">
          {view === "overview" && (
            <>
              {!isAdmin && <WelcomeBanner user={user} onExplore={() => openView({ view: "profiles" })} />}
              <DestaquesCarousel sources={sources} onSeeAll={() => openView({ view: "profiles" })} />
              <HighlightsStrip sources={sources} category={category} onSeeAll={() => openView({ view: "profiles" })} />
              <KpiCards stats={stats} onOpen={openView} />
              <DailyCollection sources={sources} stats={stats} isAdmin={isAdmin} onSelectSource={(key) => openView({ view: "profiles", source: key })} />
              <TrendChart data={stats?.trend || []} />
              {isAdmin ? (
                <div className="grid lg:grid-cols-2 gap-6">
                  <SourceChart data={stats?.per_source || []} />
                  <CityChart data={stats?.per_city || []} />
                </div>
              ) : (
                <CityChart data={stats?.per_city || []} />
              )}
            </>
          )}

          {view === "profiles" && <ProfilesTable sources={sources} category={category} initialSource={profileSource} onlyNewDefault={profileOnlyNew} defaultLayout={isAdmin ? "table" : "gallery"} initialCriteria={defaultCriteria} />}
          {view === "favorites" && <FavoritesView />}
          {view === "alerts" && <AlertsFeed sources={sources} />}
          {view === "ai" && <AISearch />}
          {view === "duplicates" && <PhoneGroups />}
          {view === "comments" && <CommentsView category={category} />}
          {view === "sources" && isAdmin && <SourcesPanel sources={sources} stats={stats} isAdmin={isAdmin} onColorsChanged={() => api.get("/sources").then((r) => setSources(r.data)).catch(() => {})} />}
        </div>
      </main>
      <WhatsAppSettingsDialog open={waSettingsOpen} onOpenChange={setWaSettingsOpen} />
      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
    </div>
    </WhatsAppProvider>
  );
}
